import React from 'react';
import { InlineMath } from 'react-katex';
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from '../../../../components/inputs/IntervalInput';
import TwoRangesOverlap from '../../../../components/visuals/TwoRangesOverlap';

/* === losowanie === */
const randInt = (a, b) => a + Math.floor(Math.random() * (b - a + 1));
const coin = () => Math.random() < 0.5;

/* === porównanie zbiorów przedziałowych === */
const byLeft = (p, q) =>
  (p.left === -Infinity ? -1 : q.left === -Infinity ? 1 : p.left - q.left);

function sameIntervals(parsed, expect) {
  if (!parsed || parsed.kind !== 'intervals' || expect.kind !== 'intervals') return false;
  const A = (parsed.intervals || []).slice().sort(byLeft);
  const B = (expect.intervals || []).slice().sort(byLeft);
  if (A.length !== B.length) return false;
  const eq = (x, y) =>
    (x === Infinity && y === Infinity) ||
    (x === -Infinity && y === -Infinity) ||
    (Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) < 1e-9);
  for (let i = 0; i < A.length; i++) {
    const I = A[i], J = B[i];
    if (!(eq(I.left, J.left) && eq(I.right, J.right) &&
          I.leftClosed === J.leftClosed && I.rightClosed === J.rightClosed)) {
      return false;
    }
  }
  return true;
}

/* === operacje na przedziałach (poprawne domknięcia) === */
function normalizeUnion(S) {
  const arr = (S.intervals || []).slice().sort(byLeft);
  const out = [];
  for (const cur of arr) {
    if (!out.length) { out.push({ ...cur }); continue; }
    const last = out[out.length - 1];
    const overlap =
      last.right > cur.left ||
      (last.right === cur.left && (last.rightClosed || cur.leftClosed));
    if (overlap) {
      if (
        cur.right > last.right ||
        (cur.right === last.right && cur.rightClosed && !last.rightClosed)
      ) {
        last.right = cur.right;
        last.rightClosed = cur.rightClosed;
      }
    } else {
      out.push({ ...cur });
    }
  }
  return { kind: 'intervals', intervals: out };
}

/** Przecięcie dwóch pojedynczych odcinków (może zwrócić null lub punkt domknięty). */
function intersect2(I, J) {
  const left = Math.max(I.left, J.left);
  const right = Math.min(I.right, J.right);

  // Domknięcie lewej krawędzi
  let leftClosed;
  if (I.left > J.left) leftClosed = I.leftClosed;
  else if (I.left < J.left) leftClosed = J.leftClosed;
  else leftClosed = I.leftClosed && J.leftClosed;

  // Domknięcie prawej krawędzi
  let rightClosed;
  if (I.right < J.right) rightClosed = I.rightClosed;
  else if (I.right > J.right) rightClosed = J.rightClosed;
  else rightClosed = I.rightClosed && J.rightClosed;

  if (left < right) return { left, right, leftClosed, rightClosed };
  if (left === right && leftClosed && rightClosed)
    return { left, right, leftClosed: true, rightClosed: true }; // singleton {left}
  return null;
}

function intersect(A, B) {
  const out = [];
  for (const I of A.intervals) {
    for (const J of B.intervals) {
      const K = intersect2(I, J);
      if (K) out.push(K);
    }
  }
  return normalizeUnion({ kind: 'intervals', intervals: out });
}

/** I \ J – zwróć listę segmentów po odjęciu J (z pełną obsługą styków). */
function subtract2(I, J) {
  const K = intersect2(I, J);
  if (!K) return [I];

  const parts = [];

  // lewy kawałek
  if (I.left < K.left || (I.left === K.left && I.leftClosed && !K.leftClosed)) {
    parts.push({
      left: I.left,
      right: K.left,
      leftClosed: I.leftClosed,
      rightClosed: !K.leftClosed,
    });
  }

  // prawy kawałek
  if (K.right < I.right || (K.right === I.right && !K.rightClosed && I.rightClosed)) {
    parts.push({
      left: K.right,
      right: I.right,
      leftClosed: !K.rightClosed,
      rightClosed: I.rightClosed,
    });
  }

  return parts;
}

function difference(A, B) {
  let current = (A.intervals || []).map(I => ({ ...I }));
  for (const J of B.intervals) {
    const next = [];
    for (const I of current) next.push(...subtract2(I, J));
    current = next;
  }
  return normalizeUnion({ kind: 'intervals', intervals: current });
}

function union(A, B) {
  return normalizeUnion({
    kind: 'intervals',
    intervals: [...(A.intervals || []), ...(B.intervals || [])],
  });
}

/* === prezentacja === */
const isEmpty = (S) => !S || !S.intervals || S.intervals.length === 0;

function setToLatex(S) {
  if (isEmpty(S)) return '\\varnothing';
  const parts = S.intervals.map(I => {
    if (I.left === I.right && I.leftClosed && I.rightClosed) return `\\{${I.left}\\}`;
    if (I.left === -Infinity) return `(-\\infty, ${I.right}${I.rightClosed ? ']' : ')'}`;
    if (I.right === Infinity) return `${I.leftClosed ? '[' : '('}${I.left}, \\infty)`;
    return `${I.leftClosed ? '[' : '('}${I.left}, ${I.right}${I.rightClosed ? ']' : ')'}`;
  });
  return parts.join(' \\cup ');
}

function toRaw(S) {
  return S.intervals.map(I =>
    I.left === I.right && I.leftClosed && I.rightClosed ? `\\{${I.left}\\}` :
    I.left === -Infinity ? `(-∞, ${I.right}${I.rightClosed ? ']' : ')'}` :
    I.right === Infinity ? `${I.leftClosed ? '[' : '('}${I.left}, ∞)` :
    `${I.leftClosed ? '[' : '('}${I.left}, ${I.right}${I.rightClosed ? ']' : ')'}`,
  ).join(' ∪ ');
}

function labeledRanges(S) {
  return S.intervals.map(I => ({
    from: I.left,
    to: I.right,
    leftClosed: I.leftClosed,
    rightClosed: I.rightClosed,
    leftLabelLatex: I.left !== -Infinity ? `${I.left}` : undefined,
    rightLabelLatex: I.right !== Infinity ? `${I.right}` : undefined,
  }));
}

/* === generatory danych === */
function makeSingle(a, b) {
  return {
    kind: 'intervals',
    intervals: [{ left: a, right: b, leftClosed: coin(), rightClosed: coin() }],
  };
}

function makeTwoSinglesDisjoint() {
  const a = randInt(-7, 2);
  const b = randInt(a + 1, a + 4);
  const c = randInt(b + 1, b + 3);
  const d = randInt(c + 1, c + 4);
  return union(makeSingle(a, b), makeSingle(c, d));
}

function makeSemiInfinite() {
  const k = randInt(-3, 8);
  const right = coin();
  const closed = coin();
  if (right) {
    return { kind: 'intervals', intervals: [{ left: k, right: Infinity, leftClosed: closed, rightClosed: false }] };
  }
  return { kind: 'intervals', intervals: [{ left: -Infinity, right: k, leftClosed: false, rightClosed: closed }] };
}

function genSimpleAB() {
  const a = randInt(-6, 4);
  const b = randInt(a + 1, a + 6);
  const c = randInt(a - 2, b + 2);
  const d = randInt(c + 1, c + 6);
  return { A: makeSingle(a, b), B: makeSingle(c, d) };
}

function genMixedAB() {
  const sum = makeTwoSinglesDisjoint();
  const inf = makeSemiInfinite();
  // chcemy: suma (pomarańcz – wyżej), półprosta (niebieski – niżej)
  return coin() ? { A: sum, B: inf } : { A: sum, B: inf };
}

/* === eksport generatorów === */
export const generateTaskSimple = () => ({ type: 'IntervalsOpsABTaskSimple', data: genSimpleAB() });
export const generateTaskMixed  = () => ({ type: 'IntervalsOpsABTaskMixed',  data: genMixedAB()  });

/* === polecenie === */
export function renderTask({ data }) {
  const { A, B } = data;
  return (
    <div style={{ fontSize: 16, lineHeight: 1.5, marginBottom: 10,  marginTop: 10 }}>
      Dane są <strong>przedziały</strong>{' '}
      <InlineMath math={`A=${intervalToLatex(toRaw(A))}`} /> oraz{' '}
      <InlineMath math={`B=${intervalToLatex(toRaw(B))}`} />. Wyznacz:
      {' '}<InlineMath math={'A\\cup B'} />,{' '}
      <InlineMath math={'A\\cap B'} />,{' '}
      <InlineMath math={'A\\setminus B'} />,{' '}
      <InlineMath math={'B\\setminus A'} />.
    </div>
  );
}

/* === input === */
export function renderInput(t, u, onChange, dis) {
  const ans = u || {};
  const row = (k, ph) => (
    <div className="flex items-start gap-2">
      <div style={{ paddingTop: 6 }}><InlineMath math={'x\\in'} /></div>
      <IntervalInput value={ans[k]} onChange={v => onChange({ ...ans, [k]: v })} disabled={dis} asString placeholder={ph} />
    </div>
  );
  return (
    <div className="space-y-2">
      <div><b>1)</b> <InlineMath math={'A\\cup B'} /></div>{row('union', 'np. (-∞,2) ∪ [5,7)')}
      <div><b>2)</b> <InlineMath math={'A\\cap B'} /></div>{row('inter', 'np. [1,3)')}
      <div><b>3)</b> <InlineMath math={'A\\setminus B'} /></div>{row('AdiffB', 'np. (-4,1] ∪ (3,∞)')}
      <div><b>4)</b> <InlineMath math={'B\\setminus A'} /></div>{row('BdiffA', 'np. (-∞,-2) ∪ (7,∞)')}
    </div>
  );
}

export function checkAnswer({ data }, user) {
  const normalizeParsed = (p) => {
    if (!p) return null;
    if (p.kind === 'empty')    return { kind: 'intervals', intervals: [] };
    if (p.kind === 'all')      return { kind: 'intervals', intervals: [{
      left: -Infinity, right: Infinity, leftClosed: false, rightClosed: false
    }]};
    return p;
  };
  const parseOne = (x) => {
    const p = (typeof x === 'string' ? parseIntervalInput(x) : x?.parsed);
    return normalizeParsed(p);
  };

  const { A, B } = data;
  const expect = {
    union:  union(A, B),
    inter:  intersect(A, B),
    AdiffB: difference(A, B),
    BdiffA: difference(B, A),
  };
  const got = {
    union:  parseOne(user?.union),
    inter:  parseOne(user?.inter),
    AdiffB: parseOne(user?.AdiffB),
    BdiffA: parseOne(user?.BdiffA),
  };
  const ok = k => sameIntervals(got[k], expect[k]);

  const uOk = ok('union');
  const iOk = ok('inter');
  const aOk = ok('AdiffB');
  const bOk = ok('BdiffA');

  const score = (uOk ? 1 : 0) + (iOk ? 1 : 0) + (aOk ? 1 : 0) + (bOk ? 1 : 0);
  const parts = { 'A∪B': uOk, 'A∩B': iOk, 'A\\B': aOk, 'B\\A': bOk };

  return { isCorrect: score === 4, score, maxScore: 4, parts };
}



/* === odpowiedź wzorcowa === */
const renderLine = (label, set) => (
  <div>
    <b>{label}:</b>{' '}
    <InlineMath math={isEmpty(set) ? 'x\\in\\;\\varnothing' : `x\\in\\;${setToLatex(set)}`} />
    </div>
);

export function renderAnswer({ data }) {
  const { A, B } = data;
  const E = {
    union:  union(A, B),
    inter:  intersect(A, B),
    AdiffB: difference(A, B),
    BdiffA: difference(B, A),
  };
  return (
    <div className="space-y-1">
      {renderLine('A ∪ B', E.union)}
      {renderLine('A ∩ B', E.inter)}
      {renderLine('A \\ B', E.AdiffB)}
      {renderLine('B \\ A', E.BdiffA)}
    </div>
  );
}

/* === wyjaśnienie (czytelne Krok 1 + multi-range na jednej osi) === */
export function renderExplanation({ data }) {
  const { A, B } = data;

  const U    = union(A, B);
  const I    = intersect(A, B);
  const AmB  = difference(A, B);
  const BmA  = difference(B, A);

  return (
    <div className="space-y-2">
      <div>
        <b>Krok 1:</b> Zapisujemy znaczenie działań na przedziałach:
        <div><InlineMath math={'A\\cup B'} /> – część osi zaznaczona przez co najmniej jeden z przedziałów (A lub B).</div>
        <div><InlineMath math={'A\\cap B'} /> – jednocześnie w A i w B (nakładanie się).</div>
        <div><InlineMath math={'A\\setminus B'} /> – tylko w A (bez fragmentów pokrywających się z B).</div>
        <div><InlineMath math={'B\\setminus A'} /> – tylko w B (bez fragmentów pokrywających się z A).</div>
      </div>

      <p><b>Krok 2:</b> Na <em>jednej osi</em> rysujemy oba przedziały:
         A – pomarańczowy (górny), B – niebieski (niższy).</p>

      <div style={{ maxWidth: 720, margin: '6px auto 10px' }}>
        <TwoRangesOverlap
          showZero
          lockPair
          pairFractions={[0.33, 0.67]}
          overlapColorPreset="pink"
          overlapOpacity={0.35}
          overlapBlur={1.5}
          ranges={[labeledRanges(A), labeledRanges(B)]}
        />
      </div>

      <p><b>Krok 3:</b> Odczyt z osi i zapis przedziałowy:</p>
      <div className="space-y-1">
        {renderLine('A ∪ B', U)}
        {renderLine('A ∩ B', I)}
        {renderLine('A \\ B', AmB)}
        {renderLine('B \\ A', BmA)}
      </div>
    </div>
  );
}

/* === odpowiedź użytkownika (używane w pliku zbiorczym) === */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer) return <i>(brak odpowiedzi)</i>;
  const show = (key, label) => {
    const raw = typeof userAnswer[key] === 'string' ? userAnswer[key] : userAnswer[key]?.raw;
    if (!raw) return (<div><strong>{label}:</strong> <i>(brak)</i></div>);
    return (
      <div>
        <strong>{label}:</strong>{' '}
        <InlineMath math={`x\\in\\;${intervalToLatex(raw)}`} />
      </div>
    );
  };
  return (
    <div className="space-y-1">
      {show('union',  'A ∪ B')}
      {show('inter',  'A ∩ B')}
      {show('AdiffB', 'A \\ B')}
      {show('BdiffA', 'B \\ A')}
    </div>
  );
}
